import { Search } from '@mui/icons-material';
import { Button, Divider, Radio, Skeleton, Stack, TableCell, TableRow, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useUserNodes } from '/@/hooks/nodes';
import { _ } from '/@/locale';

const GrowerSelectorModal = ({
    userNodeId,
    selectedUserNodeId,
    setSelectedUserNodeId,
    onBack,
    text,
    nextAction,
    ModalV2Props,
}: {
    text: string;
    userNodeId: number;
    selectedUserNodeId: number | null;
    setSelectedUserNodeId: (selectedUserNodeId: number) => void;
    onBack?: () => void;
    nextAction: {
        onClick: () => void;
        text: string;
    };
    ModalV2Props: React.ComponentProps<typeof ModalV2>;
}) => {
    const [search, setSearch] = useState('');

    const userNodesQuery = useUserNodes({
        page_size: 10000,
        page: 1,
        node: userNodeId,
        type: 'grower',
        excludeTargetNode: true,
    });

    const filteredUserNodes = useMemo(() => {
        return userNodesQuery.data?.results
            .map((userNode) => ({
                ...userNode,
            }))
            .filter((userNode) => userNode.name.toLowerCase().includes(search.toLowerCase()));
    }, [userNodesQuery.data, search]);

    return (
        <ModalV2
            actions={
                <>
                    {onBack && (
                        <Button variant='text' color='secondary' onClick={onBack}>
                            {_('Atras')}
                        </Button>
                    )}
                    {nextAction && (
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={nextAction.onClick}
                            disabled={!selectedUserNodeId}
                            data-testid='add-field-button-next'
                        >
                            {nextAction.text}
                        </Button>
                    )}
                </>
            }
            maxWidth='md'
            {...ModalV2Props}
        >
            <Typography variant='subtitle1' color='primary'>
                {text}
            </Typography>
            <TextField
                InputProps={{
                    startAdornment: <Search />,
                }}
                placeholder={_('Busca el productor por el nombre')}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                fullWidth
            />
            <Stack className='mp-mask' divider={<Divider />} width='100%'>
                {userNodesQuery.isValidating ? (
                    Array.from({ length: 10 }, (_, index) => {
                        // Using index for skeleton loaders is acceptable as they're temporary placeholders
                        const skeletonKey = `skeleton-loader-${index}`;
                        return <Skeleton variant='rectangular' height={100} key={skeletonKey} />;
                    })
                ) : filteredUserNodes?.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={3}>
                            <Typography variant='body1' color='primary'>
                                {_('No se encontraron productores')}
                            </Typography>
                        </TableCell>
                    </TableRow>
                ) : (
                    filteredUserNodes?.map((userNode, index) => (
                        <Stack
                            direction='row'
                            key={userNode.id}
                            onClick={() => setSelectedUserNodeId(userNode.id)}
                            sx={[
                                {
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                },
                                selectedUserNodeId === userNode.id && {
                                    backgroundColor: 'action.selected',
                                },
                            ]}
                        >
                            <Radio
                                data-testid={`grower-selector-modal-radio-${index}`}
                                value={userNode.id}
                                checked={selectedUserNodeId === userNode.id}
                                onChange={() => setSelectedUserNodeId(userNode.id)}
                            />
                            <Stack spacing={0.5} px={1.5} py={1}>
                                <Typography variant='body1' color='primary'>
                                    {userNode.isSelf ? _('Mi cuenta') : userNode.name}
                                </Typography>
                                {userNode.location && (
                                    <Typography variant='caption' color='primary.mui.inactive'>
                                        {userNode.location}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    ))
                )}
            </Stack>
        </ModalV2>
    );
};

export default GrowerSelectorModal;
